import { useMemo, useReducer } from 'react';

import { createDraft, draftReducer } from '../utils/draftReducer';

import type {
    CompensationDraftAction,
    CompensationDraftFields,
    CompensationDraftState,
} from '../utils/draftReducer';
import type {
    CompensationDraftLine,
    CompensationDraftRequest,
    CompensationEnvelope,
} from '@/types/compensation';
import type { Dispatch } from 'react';

/**
 * A comparable, order-independent projection of the draft's editable fields. Key order carries no
 * meaning here — a multiplier set and then set back, or an audience filter re-added in another
 * order, must not read as a change — so every map and list is sorted by its own identifier before
 * being serialized.
 */
const canonicalize = (fields: CompensationDraftFields): string =>
    JSON.stringify({
        name: fields.name,
        audienceType: fields.audience.type,
        audienceFilters: [...fields.audience.filters]
            .sort((a, b) => a.attribute.localeCompare(b.attribute))
            .map((filter) => [filter.attribute, [...filter.valueIds].sort()]),
        budgetAmount: fields.budgetAmount,
        budgetInputMode: fields.budgetInputMode,
        distributionMode: fields.distributionMode,
        scoreThreshold: fields.scoreThreshold,
        scoreSpread: fields.scoreSpread,
        levelMultipliers: Object.keys(fields.levelMultipliers)
            .sort()
            .map((levelId) => [levelId, fields.levelMultipliers[levelId]]),
        lines: [...fields.lines]
            .sort((a, b) => a.employeeId.localeCompare(b.employeeId))
            .map((line) => [
                line.employeeId,
                line.salary,
                line.chosenPercent,
                line.isLocked,
                line.lockedAmount,
                line.note,
            ]),
    });

/**
 * The lines as a request carries them: a salary, and its source, only where the reader changed it.
 * Every other line sends none and the server keeps what the cycle holds — a draft opened before a
 * correction made in another envelope of the cycle must not write the old figure back on save.
 */
export const toRequestLines = (
    lines: CompensationDraftLine[],
    baselineLines: CompensationDraftLine[],
): CompensationDraftLine[] => {
    const baselineById = new Map(baselineLines.map((line) => [line.employeeId, line]));

    return lines.map((line) => {
        const baseline = baselineById.get(line.employeeId);
        const isSalaryChanged =
            baseline === undefined ||
            baseline.salary !== line.salary ||
            baseline.salarySource !== line.salarySource;

        return isSalaryChanged ? line : { ...line, salary: null, salarySource: null };
    });
};

export interface UseEnvelopeDraftResult {
    draft: CompensationDraftState;
    dispatch: Dispatch<CompensationDraftAction>;
    /** True as soon as the draft differs structurally from the envelope it was seeded from. */
    isDirty: boolean;
    /** The draft as the preview and save endpoints expect it. */
    draftRequest: CompensationDraftRequest;
}

/**
 * Owns the local draft of an envelope: every setting and line input edited before "Sauvegarder".
 * Seeded once, when mounted: the caller keys it by envelope id and version, so a save (which bumps
 * the version) starts a fresh draft while a refetch of the same version leaves the draft alone.
 */
export const useEnvelopeDraft = (envelope: CompensationEnvelope): UseEnvelopeDraftResult => {
    const [draft, dispatch] = useReducer(draftReducer, envelope, createDraft);

    const savedFingerprint = useMemo(() => canonicalize(draft.baseline), [draft.baseline]);
    const isDirty = useMemo(
        () => canonicalize(draft) !== savedFingerprint,
        [draft, savedFingerprint],
    );

    const draftRequest = useMemo<CompensationDraftRequest>(
        () => ({
            name: draft.name,
            audience: draft.audience,
            budgetAmount: draft.budgetAmount,
            budgetInputMode: draft.budgetInputMode,
            distributionMode: draft.distributionMode,
            scoreThreshold: draft.scoreThreshold,
            scoreSpread: draft.scoreSpread,
            levelMultipliers: draft.levelMultipliers,
            lines: toRequestLines(draft.lines, draft.baseline.lines),
        }),
        [draft],
    );

    return { draft, dispatch, isDirty, draftRequest };
};
